import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { appointmentAPI } from '../lib/api';
import { formatShortDate } from '../lib/utils';
import { 
  MessageCircle, 
  Send, 
  Phone, 
  User,
  Calendar,
  AlertTriangle,
  Loader2,
  Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WhatsAppSection() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [configStatus, setConfigStatus] = useState(null);
  
  const [message, setMessage] = useState({
    to_phone: '',
    message_text: ''
  });

  const templates = [
    { id: 'reminder', name: 'Promemoria Appuntamento', text: 'Ciao! Ti ricordiamo il tuo appuntamento di domani alle {time}. Ti aspettiamo! 💈' },
    { id: 'confirmation', name: 'Conferma Prenotazione', text: 'La tua prenotazione è confermata per il {date} alle {time}. Grazie! ✅' },
    { id: 'cancellation', name: 'Cancellazione', text: 'Il tuo appuntamento del {date} è stato cancellato. Contattaci per una nuova prenotazione.' },
    { id: 'custom', name: 'Messaggio Personalizzato', text: '' }
  ];

  useEffect(() => {
    loadData();
    checkConfig();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await appointmentAPI.list({ limit: 20 });
      const upcoming = (res.data || []).filter(apt => apt.guest_phone || apt.user_phone);
      setAppointments(upcoming);
    } catch (error) {
      console.error('Errore caricamento:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/whatsapp/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_phone: 'test', message: 'test' })
      });
      const data = await res.json();
      if (data.detail?.includes('non configurato') || data.detail?.includes('API key')) {
        setConfigStatus('not_configured');
      } else {
        setConfigStatus('configured');
      }
    } catch (error) {
      setConfigStatus('unknown');
    }
  };

  const handleSendMessage = async () => {
    if (!message.to_phone || !message.message_text) {
      toast.error('Inserisci numero e messaggio');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/whatsapp/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_phone: message.to_phone, message: message.message_text })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Errore invio');
      toast.success('Messaggio inviato!');
      setDialogOpen(false);
      setMessage({ to_phone: '', message_text: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  const handleNotifyAppointment = async (appointmentId) => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/whatsapp/notify-appointment?appointment_id=${appointmentId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Errore invio');
      toast.success('Notifica inviata al cliente!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  const applyTemplate = (template) => {
    setMessage(prev => ({ ...prev, message_text: template.text }));
  };

  return (
    <div className="space-y-6">
      {configStatus === 'not_configured' && (
        <Alert className="border-yellow-500 bg-yellow-50">
          <AlertTriangle className="w-4 h-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <p className="font-medium">Twilio non configurato</p>
            <p className="text-sm mt-1">Per inviare messaggi WhatsApp, configura le credenziali Twilio nel file .env:</p>
            <ul className="text-sm mt-2 list-disc list-inside">
              <li>TWILIO_ACCOUNT_SID</li>
              <li>TWILIO_AUTH_TOKEN</li>
              <li>TWILIO_WHATSAPP_FROM</li>
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-500" />
              Messaggi WhatsApp
            </CardTitle>
            <CardDescription>Invia notifiche ai clienti tramite WhatsApp</CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-whatsapp-btn">
                <Send className="w-4 h-4 mr-2" />
                Nuovo Messaggio
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Invia Messaggio WhatsApp</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Numero telefono</Label>
                  <Input value={message.to_phone} onChange={(e) => setMessage({...message, to_phone: e.target.value})} placeholder="+39 333 1234567" data-testid="whatsapp-phone-input" />
                  <p className="text-xs text-muted-foreground mt-1">Inserisci il numero con prefisso internazionale</p>
                </div>
                <div>
                  <Label>Template</Label>
                  <Select onValueChange={(v) => { const t = templates.find(t => t.id === v); if (t) applyTemplate(t); }}>
                    <SelectTrigger><SelectValue placeholder="Seleziona template..." /></SelectTrigger>
                    <SelectContent>
                      {templates.map(t => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Messaggio</Label>
                  <Textarea value={message.message_text} onChange={(e) => setMessage({...message, message_text: e.target.value})} placeholder="Scrivi il tuo messaggio..." rows={4} data-testid="whatsapp-message-input" />
                  <p className="text-xs text-muted-foreground mt-1">{message.message_text.length} / 1000 caratteri</p>
                </div>
                <Button onClick={handleSendMessage} className="w-full bg-green-600 hover:bg-green-700" disabled={sending || configStatus === 'not_configured'} data-testid="send-whatsapp-btn">
                  {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Invio...</> : <><Send className="w-4 h-4 mr-2" />Invia WhatsApp</>}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-4 bg-muted/50 rounded-lg">
              <Info className="w-5 h-5 text-blue-500" />
              <p className="text-sm">Invia rapidamente notifiche ai clienti con appuntamenti</p>
            </div>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : appointments.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Nessun appuntamento con contatto telefonico</p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm font-medium">Appuntamenti recenti:</p>
                {appointments.slice(0, 5).map(apt => (
                  <div key={apt.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`apt-whatsapp-${apt.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"><User className="w-5 h-5 text-primary" /></div>
                      <div>
                        <p className="font-medium">{apt.guest_name || apt.user_name || 'Cliente'}</p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatShortDate(apt.date)} {apt.time}</span>
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{apt.guest_phone || apt.user_phone}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{apt.status}</Badge>
                      <Button size="sm" variant="outline" className="text-green-600 border-green-600 hover:bg-green-50" onClick={() => handleNotifyAppointment(apt.id)} disabled={sending || configStatus === 'not_configured'}>
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Configurazione Twilio</CardTitle></CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            <li>Crea un account su <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">twilio.com</a></li>
            <li>Attiva WhatsApp Sandbox o acquista un numero WhatsApp Business</li>
            <li>Copia Account SID e Auth Token dalla dashboard</li>
            <li>Configura le variabili nel file .env del backend</li>
            <li>Riavvia il backend per applicare le modifiche</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
